/* eslint-disable react-hooks/exhaustive-deps */
import useSnackbar from '@/hooks/useSnackbar';
import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  ApprovalStatus,
  DocumentCategory,
  DocumentStatus,
  StatusCode,
} from '../../../../../api/enumerations';
import {
  approveContractDocument,
  approveNoticeCompany,
  approveNoticeCompanyDocument,
  cancelExtraDocument,
  getNoticeCompany,
  getNoticeCompanyDocuments,
  rejectContractDocument,
  rejectNoticeCompanyDocument,
  replaceNoticeCompanyDocument,
} from '../../../../../api/theHive/notices';
import {
  CancelExtraDocumentData,
  NoticeDocumentsData,
  RejectDocumentData,
  RequestNoticeCompany,
} from '../../../../../api/theHive/notices/types';
import StandardMap from '../../../../../components/Maps/StandardMap';
import { WhiteCircularProgress } from '../../../../../components/UI/Typography';
import { IconArrowCircleLeftMS } from '../../../../../constants/icons';
import { matchCompanySubdomain } from '../../../../../helpers';
import { useStoragedJwt } from '../../../../../hooks/useDecodedJwt';
import useErrorMessage from '../../../../../hooks/useErrorMessage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import AcceptanceDocsTable from './components/AcceptanceDocsTable';
import { CompanyCard } from './components/CompanyData';
import ContractTable from './components/ContractTable';
import DocumentsTable from './components/DocumentsTable';
import { RequestDocument } from './components/RequestDocument';
import { CustomBackButton, GridSection } from './styles';

type LatLngLiteral = google.maps.LatLngLiteral;

export function LicenseRequest(): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [pendingDocError, setPendingDocError] = useState(false);
  const [companyData, setCompanyData] = useState<
    RequestNoticeCompany | undefined
  >();

  const [legalDocs, setLegalDocs] = useState<NoticeDocumentsData[]>([]);
  const [legalPersonDocs, setLegalPersonDocs] = useState<NoticeDocumentsData[]>(
    []
  );
  const [professionalDocs, setProfessionallDocs] = useState<
    NoticeDocumentsData[]
  >([]);
  const [requiredForBBOrCaixaDocs, setRequiredForBBOrCaixaDocs] = useState<
    NoticeDocumentsData[]
  >([]);
  const [extraDocs, setExtraDocs] = useState<NoticeDocumentsData[]>([]);

  const [activeTab, setActiveTab] = useState(0);

  const [searchMap, setSearchMap] = useState(true);
  const [pinPlace, setPinPlace] = useState<LatLngLiteral>({
    lat: -23.56162,
    lng: -46.65591,
  });

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { notice: noticeIdParam, request, company } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);
  const requestId = parseInt(request as string, 10);
  const companyId = parseInt(company as string, 10);
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const isPendingStatus =
    companyData?.approval_status === ApprovalStatus.PENDING;
  const showFormalizationTables =
    companyData?.approval_status === ApprovalStatus.AWAITING_FORMALIZATION ||
    companyData?.approval_status === ApprovalStatus.FORMALIZATION_SUBMITTED ||
    companyData?.approval_status === ApprovalStatus.APPROVED;

  const navigateBack = (): void => {
    if (pathname.includes('company-details') && companyId) {
      navigate(`/the-hive/licensed/company-details/${companyId}`);
    } else {
      navigate(`/the-hive/notices/${noticeId}`);
    }
  };

  const handleLicenseRequest = async (loadPage = false): Promise<void> => {
    if (loadPage) setLoading(true);
    try {
      const response = await getNoticeCompany(requestId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setCompanyData(response.data);

      const resp = await getNoticeCompanyDocuments(requestId);

      if (resp.detail.description) throw new Error(resp.detail.description);

      if (!resp.data) throw new Error('Algo deu errado, tente novamente.');

      const pending = resp.data.filter(
        (data) =>
          data.status !== DocumentStatus.APPROVED &&
          data.status !== DocumentStatus.CANCELED
      );
      const legal = resp.data.filter(
        (data) => data.document_category === DocumentCategory.LEGAL
      );
      const technical = resp.data.filter(
        (data) => data.document_category === DocumentCategory.TECHNICAL
      );
      const professional = resp.data.filter(
        (data) => data.document_category === DocumentCategory.PROFESSIONAL
      );
      const bbOrCaixa = resp.data.filter(
        (data) => data.document_category === DocumentCategory.BB
      );
      const others = resp.data.filter(
        (data) => data.document_category === DocumentCategory.OTHER
      );

      setLegalDocs(legal);
      setLegalPersonDocs(technical);
      setProfessionallDocs(professional);
      setRequiredForBBOrCaixaDocs(bbOrCaixa);
      setExtraDocs(others);

      const hasPendingDocuments = !!pending.length || !resp.data.length;
      setPendingDocError(hasPendingDocuments);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigateBack();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (noticeId) handleLicenseRequest(true);
  }, [noticeId, requestId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (companyData) {
        setSearchMap(!searchMap);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [companyData]);

  const handleApproveCompany = async (): Promise<void> => {
    if (pendingDocError) {
      handleSnackbar(
        'Há documentos não aprovados, conclua a análise dos documentos.',
        true
      );
      return;
    }

    try {
      setApproveLoading(true);
      const response = await approveNoticeCompany(requestId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      handleSnackbar('Formalização iniciada com sucesso!', false);
      handleLicenseRequest();
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setApproveLoading(false);
    }
  };

  const withDocLoading = async (
    fn: () => Promise<{
      detail: { description?: string; status_code: number };
    }>,
    successMsg: string,
    onSuccess?: () => void
  ): Promise<void> => {
    setDocLoading(true);
    try {
      const response = await fn();
      if (response.detail.description)
        throw new Error(response.detail.description);
      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');
      handleSnackbar(successMsg, false);
      if (onSuccess) {
        onSuccess();
      } else {
        handleLicenseRequest();
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setDocLoading(false);
    }
  };

  const handleApproveDocument = async (docId?: number): Promise<void> => {
    const successMessage = docId
      ? 'Documento aprovado!'
      : 'Credenciamento aprovado com sucesso!';
    const onSuccess = docId ? undefined : navigateBack;
    await withDocLoading(
      () =>
        docId
          ? approveNoticeCompanyDocument(requestId, docId)
          : approveContractDocument(requestId),
      successMessage,
      onSuccess
    );
  };

  const handleReplaceDocument = async (docId: number): Promise<void> => {
    await withDocLoading(
      () => replaceNoticeCompanyDocument(requestId, docId),
      'Substituição solicitada!'
    );
  };

  const handleRejectDocument = async (
    reason: string,
    docId?: number
  ): Promise<void> => {
    const data: RejectDocumentData = { rejection_reason: reason };
    await withDocLoading(
      () =>
        docId
          ? rejectNoticeCompanyDocument(requestId, docId, data)
          : rejectContractDocument(requestId, data),
      'Documento rejeitado!'
    );
  };

  const handleCancelExtraDocument = async (docId: number): Promise<void> => {
    const data: CancelExtraDocumentData = {
      noticeCompanyId: requestId,
      documentId: docId,
    };
    await withDocLoading(
      () => cancelExtraDocument(data),
      'Documento cancelado!'
    );
  };

  const legalSections = [
    { name: 'Análise jurídica', docList: legalDocs },
    { name: 'Análise técnica da Pessoa Jurídica', docList: legalPersonDocs },
  ];

  const engineeringSections = [
    { name: 'Análise técnica do profissional', docList: professionalDocs },
    {
      name: 'Documentos de qualificação e habilitação profissional',
      docList: requiredForBBOrCaixaDocs,
    },
  ];

  // RBAC das abas de análise (REAL-3099): Jurídica → jurídico (17); Engenharia
  // → engenheiro (2). Admin da plataforma (3) e admin do banco (10) veem ambas.
  // O enum de papéis completo e o helper de acesso vivem na branch de RBAC;
  // aqui usamos os valores diretamente para manter esta branch autônoma.
  const ROLE_ADMIN = 3;
  const ROLE_BANK_ADMIN = 10;
  const ROLE_ENGINEER = 2;
  const ROLE_JURIDICO = 17;
  const currentRole = useStoragedJwt()?.user.companies.find(
    (item) => item.subdomain === matchCompanySubdomain()
  )?.role;
  const isAdmin =
    currentRole === ROLE_ADMIN || currentRole === ROLE_BANK_ADMIN;

  const analysisTabs = [
    { label: 'Análise Jurídica', sections: legalSections, role: ROLE_JURIDICO },
    {
      label: 'Análise Engenharia',
      sections: engineeringSections,
      role: ROLE_ENGINEER,
    },
  ].filter(
    (tab) => isAdmin || (currentRole != null && tab.role === currentRole)
  );

  const currentTab = activeTab < analysisTabs.length ? activeTab : 0;
  const activeSections = analysisTabs[currentTab]?.sections ?? [];

  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <CustomBackButton onClick={navigateBack}>
        {IconArrowCircleLeftMS}
      </CustomBackButton>
      <Box mt={5}>
        {companyData && (
          <Grid container spacing={3}>
            <Grid item xs={7}>
              <CompanyCard
                noticeId={noticeId}
                data={companyData}
                callback={handleLicenseRequest}
              />
            </Grid>
            <Grid item xs={5}>
              <GridSection container>
                <Grid item xs={12}>
                  <StandardMap
                    searchMap={searchMap}
                    address={`${companyData.company.address} ${companyData.company.address_number}`}
                    district={companyData.company.address_district}
                    city={companyData.company.city}
                    pinPlace={pinPlace}
                    setPinPlace={setPinPlace}
                    draggable={false}
                  />
                </Grid>
              </GridSection>
            </Grid>
          </Grid>
        )}
        <Typography my={5} fontSize={21} fontWeight={700}>
          Documentação necessária:
        </Typography>
        {analysisTabs.length > 0 && (
          <Tabs
            value={currentTab}
            onChange={(_, value) => setActiveTab(value)}
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ mb: 3 }}
          >
            {analysisTabs.map((tab) => (
              <Tab
                key={tab.label}
                label={tab.label}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16 }}
              />
            ))}
          </Tabs>
        )}
        <RequestDocument
          dataCallback={handleLicenseRequest}
          disabled={!isPendingStatus}
        />
        {activeSections.map(({ name, docList }) => (
          <DocumentsTable
            key={name}
            name={name}
            docList={docList}
            docLoading={docLoading}
            handleReplace={handleReplaceDocument}
            handleApprove={handleApproveDocument}
            handleReject={handleRejectDocument}
            disabled={!isPendingStatus}
          />
        ))}
        {extraDocs.length > 0 && (
          <DocumentsTable
            name="Documentos adicionais"
            docList={extraDocs}
            docLoading={docLoading}
            handleReplace={handleReplaceDocument}
            handleApprove={handleApproveDocument}
            handleReject={handleRejectDocument}
            handleCancel={handleCancelExtraDocument}
            disabled={!isPendingStatus}
          />
        )}
        {showFormalizationTables && (
          <>
            <ContractTable
              name="Contrato de prestação de serviço"
              data={companyData.signed_contract}
              docLoading={docLoading}
              handleApprove={handleApproveDocument}
              handleReject={handleRejectDocument}
            />
            <AcceptanceDocsTable docList={companyData.acceptances || []} />
          </>
        )}
        {isPendingStatus && (
          <Box width="100%" my={10} textAlign="center">
            <Button
              sx={{ width: '545px' }}
              disabled={approveLoading}
              onClick={handleApproveCompany}
            >
              {approveLoading ? (
                <WhiteCircularProgress size={22} />
              ) : (
                'Avançar para formalização'
              )}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}
